import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Calendar, 
  MapPin, 
  Search, 
  Loader2,
  Clock,
  Trash2,
  ArrowRight,
  History
} from 'lucide-react';
import { format } from 'date-fns';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function PastEvents() {
  const { user, isAdmin } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isSuperAdmin = user?.admin_role === 'super_admin';

  useEffect(() => {
    fetchPastEvents();
  }, []);

  const fetchPastEvents = async (searchQuery = '') => {
    setLoading(true);
    try {
      const params = { limit: 50 };
      if (searchQuery) params.search = searchQuery;
      
      const response = await axios.get(`${API_URL}/api/events/past`, { params });
      setEvents(response.data);
    } catch (error) {
      console.error('Error fetching past events:', error);
      toast.error('Failed to load past events');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchPastEvents(search);
  };

  const handleDeleteClick = (event) => {
    setEventToDelete(event);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;
    
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/events/admin/past/${eventToDelete.event_id}`,
        
      );
      toast.success('Past event deleted successfully');
      setDeleteModalOpen(false);
      setEventToDelete(null);
      // Refresh the list
      fetchPastEvents(search);
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Date TBA';
    try {
      return format(new Date(dateStr), 'EEE, MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getImageUrl = (url) => {
    if (!url) return 'https://images.unsplash.com/photo-1563841930606-67e2bce48b78?auto=format&fit=crop&q=80&w=800';
    if (url.startsWith('http')) return url;
    return `${API_URL}${url}`;
  };

  return (
    <Layout>
      <div className="min-h-[calc(100vh-4rem)] bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="past-events-page">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
            <History className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Past Events</h1>
          <p className="text-muted-foreground">
            Browse events that have already taken place
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 max-w-md mx-auto mb-8">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search past events..."
              className="pl-10"
              data-testid="past-events-search"
            />
          </div>
          <Button type="submit">Search</Button>
        </form>

        {/* Events Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : events.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {events.map((event) => (
              <Card key={event.event_id} className="overflow-hidden bg-card border-border group">
                <div className="relative aspect-video overflow-hidden">
                  <img
                    src={getImageUrl(event.image_url)}
                    alt={event.title}
                    className="w-full h-full object-cover transition-transform group-hover:scale-105"
                  />
                  <Badge className="absolute top-3 left-3 bg-muted/90 text-muted-foreground">
                    <Clock className="w-3 h-3 mr-1" />
                    Past Event
                  </Badge>
                  {isSuperAdmin && (
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={(e) => {
                        e.preventDefault();
                        handleDeleteClick(event);
                      }}
                      data-testid={`delete-event-${event.event_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg mb-2 line-clamp-1">{event.title}</h3>
                  
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(event.date)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span className="line-clamp-1">{event.venue || 'Venue TBA'}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      {event.category || 'Event'}
                    </Badge>
                    <Link to={`/events/${event.event_id}`}>
                      <Button variant="ghost" size="sm" className="gap-1">
                        View Details
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-card border-border">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <History className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No past events found</h3>
              <p className="text-muted-foreground text-center mb-4">
                {search ? 'Try a different search term' : 'There are no past events to display'}
              </p>
              <Link to="/events">
                <Button variant="outline">Browse Upcoming Events</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Delete Confirmation Modal */}
        <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete Past Event</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete "{eventToDelete?.title}"? This will also delete all associated tickets and transactions. This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleDeleteConfirm}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Event
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      </div>
    </Layout>
  );
}
